import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  NumberInput,
  Pagination,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconFileTypePdf,
  IconFileZip,
  IconPlayerPause,
  IconPlayerPlay,
  IconRefresh,
  IconTableExport,
} from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { LogoPaper, PageTitle, RatingBadge, SortableTh, StatusBadge } from '@/components';
import { DETAIL_PAGE_SIZE, useScreeningBatch, useSearchDraft, useTeams } from '@/hooks';
import { vnextApiClient } from '@/services/vnext-api';
import {
  SCREENING_RATING_FILTER_OPTIONS,
  SCREENING_RATING_META,
  ScreeningBatchReportRow,
  ScreeningBatchStatus,
  ScreeningRating,
  ScreeningRatingCounts,
  summariseScreeningCounts,
  UNRATED_FILTER_OPTION,
} from '@/types';
import { downloadCsvFile, saveUrlAs } from '@/utils';

/** Register filter values: the three ratings plus "not rated yet". */
type RatingFilter = ScreeningRating | 'unrated' | '';

const RATING_FILTER_OPTIONS = [...SCREENING_RATING_FILTER_OPTIONS, UNRATED_FILTER_OPTION];

/** Whole-batch hit / potential / clear tallies (unaffected by the filter). */
function RatingTallies({ ratings }: Readonly<{ ratings: ScreeningRatingCounts }>) {
  const unrated = ratings.unrated > 0 ? ` · ${ratings.unrated} not rated yet` : '';
  return (
    <Text size="sm" c="dimmed" data-testid="batch-rating-tallies">
      {ratings.hit} hit · {ratings.potential} potential · {ratings.clear} clear
      {unrated}
    </Text>
  );
}

const BATCH_STATUS_COLORS: Record<ScreeningBatchStatus, string> = {
  pending: 'gray',
  running: 'blue',
  paused: 'yellow',
  completed: 'green',
};

const BATCH_STATUS_LABELS: Record<ScreeningBatchStatus, string> = {
  pending: 'Not started',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
};

/** Batch-context lifecycle labels: TRIAGE means "awaiting dispatch". */
const REPORT_STATUS: Record<string, { label: string; color: string }> = {
  triage: { label: 'Pending dispatch', color: 'gray' },
  pending_activation: { label: 'Pending activation', color: 'orange' },
  queued: { label: 'Queued', color: 'gray' },
  processing: { label: 'Processing', color: 'blue' },
  processing_feedback: { label: 'Processing', color: 'blue' },
  awaiting_fulfilment: { label: 'With Network Partner', color: 'indigo' },
  awaiting_file_selection: { label: 'Awaiting file selection', color: 'violet' },
  awaiting_profile_review: { label: 'Awaiting profile review', color: 'orange' },
  awaiting_match_review: { label: 'Awaiting match review', color: 'grape' },
  in_qa: { label: 'In QA', color: 'cyan' },
  completed: { label: 'Complete', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
};

export function ScreeningBatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  // Register view state lives in the URL (reports-list idiom), so Back
  // returns to the exact search/sort/filter/page the operator left.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const rating = (searchParams.get('rating') ?? '') as RatingFilter;
  const hrjFilter = searchParams.get('hrj') ?? '';
  const search = searchParams.get('search') ?? '';
  const status = searchParams.get('status') ?? '';
  const sort = searchParams.get('sort') ?? '';

  const updateParams = useCallback(
    (updates: Record<string, string | number>, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === '' || value === 0) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        opts?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );
  // Debounced draft → URL commit (replace — Back should not step through
  // every settled search value).
  const [searchValue, setSearchValue] = useSearchDraft(search, (settled) =>
    updateParams({ search: settled, page: 1 }, { replace: true })
  );
  const handleSort = useCallback(
    (field: string) => {
      const currentField = sort.replace('-', '');
      const isDesc = sort.startsWith('-');
      updateParams({ sort: currentField === field ? (isDesc ? field : `-${field}`) : `-${field}` });
    },
    [sort, updateParams]
  );
  const sortProps = {
    sortBy: sort.replace('-', ''),
    sortOrder: (sort.startsWith('-') ? 'desc' : 'asc') as 'asc' | 'desc',
    onSort: handleSort,
  };

  const { batch, isLoading, error, start, pause, resume, setWindow, fetchAllRows } =
    useScreeningBatch(batchId, {
      page,
      pageSize: DETAIL_PAGE_SIZE,
      rating,
      hrj: hrjFilter === '' ? undefined : hrjFilter === 'true',
      search,
      status,
      sort,
    });
  const [windowDraft, setWindowDraft] = useState<number | string>('');
  const [exporting, setExporting] = useState(false);
  // Whole-batch zip: server-side job; poll the manifest while it builds.
  const [zipStatus, setZipStatus] = useState<import('@/types').ScreeningBatchExportStatus | null>(
    null
  );
  const zipTimer = useRef<number | null>(null);
  const { teams } = useTeams();

  const pollZip = useCallback(
    async (notify: boolean) => {
      if (!batchId) {
        return;
      }
      try {
        const manifest = await vnextApiClient.getScreeningBatchExport(batchId);
        setZipStatus(manifest);
        if (manifest.status === 'running') {
          zipTimer.current = window.setTimeout(() => void pollZip(notify), 2500);
        } else if (notify && manifest.status === 'complete') {
          notifications.show({
            title: 'Batch export ready',
            message: `${manifest.done} report PDFs + register.csv`,
            color: 'green',
          });
        } else if (notify && manifest.status === 'error') {
          notifications.show({
            title: 'Batch export failed',
            message: manifest.error ?? 'Unknown error',
            color: 'red',
          });
        }
      } catch (err) {
        // 404 = never exported — the button just reads "Download all".
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status !== 404) {
          Sentry.captureException(err);
        }
        setZipStatus(null);
      }
    },
    [batchId]
  );

  // Attach to any existing export on mount, so navigating away and back
  // resumes the progress view (or offers the finished zip) instead of
  // erroring.
  useEffect(() => {
    void pollZip(false);
  }, [pollZip]);

  useEffect(
    () => () => {
      if (zipTimer.current !== null) {
        window.clearTimeout(zipTimer.current);
      }
    },
    []
  );

  const startZipBuild = async () => {
    if (!batchId) {
      return;
    }
    try {
      const manifest = await vnextApiClient.startScreeningBatchExport(batchId);
      setZipStatus(manifest);
      zipTimer.current = window.setTimeout(() => void pollZip(true), 2500);
    } catch (err) {
      // 409 = a build is already running — just start following it.
      const axiosStatus = (err as { response?: { status?: number } }).response?.status;
      if (axiosStatus === 409) {
        void pollZip(true);
        return;
      }
      const e = err instanceof Error ? err : new Error('Export failed');
      Sentry.captureException(e);
      notifications.show({
        title: 'Could not start batch export',
        message: e.message,
        color: 'red',
      });
    }
  };

  const saveZip = async () => {
    if (!batchId) {
      return;
    }
    try {
      // Re-read for a fresh presigned URL — stored links go stale.
      const manifest = await vnextApiClient.getScreeningBatchExport(batchId);
      setZipStatus(manifest);
      if (manifest.status === 'complete' && manifest.url) {
        await saveUrlAs(manifest.url, manifest.filename ?? 'batch-export.zip');
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const teamName = useMemo(() => {
    if (!batch) {
      return '';
    }
    return teams.find((team) => String(team.id) === batch.team_id)?.name ?? batch.team_id;
  }, [teams, batch]);

  const download = async (row: ScreeningBatchReportRow, kind: 'docx' | 'pdf') => {
    try {
      const { url, filename } =
        kind === 'docx'
          ? await vnextApiClient.downloadReportDocx(row.report_id)
          : await vnextApiClient.downloadReportPdf(row.report_id);
      await saveUrlAs(url, filename);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  // The register is every row, not the page on screen: walk the pages
  // once, on click (never polled).
  const exportRegister = async () => {
    if (!batch) {
      return;
    }
    setExporting(true);
    try {
      const all = await fetchAllRows();
      const rows: string[][] = [
        [
          'Reference',
          'Subject',
          'Type',
          'Jurisdiction',
          'Date of birth',
          'Status',
          'Rating',
          'HRJ',
          'Findings',
          'Codename',
          'Report link',
          'Completed at',
          // Last, so every earlier column keeps its place for readers
          // that index the register by position.
          'IMO',
        ],
        ...all.map((row) => [
          row.reference ?? '',
          row.subject_name,
          row.subject_type ?? '',
          row.jurisdictions.join(' '),
          row.date_of_birth ?? '',
          REPORT_STATUS[row.lifecycle_stage]?.label ?? row.lifecycle_stage,
          row.screening_rating ? SCREENING_RATING_META[row.screening_rating].label : '',
          row.high_risk_jurisdictions.join(' '),
          row.screening_counts ? summariseScreeningCounts(row.screening_counts) : '',
          row.codename ?? '',
          `${window.location.origin}/reports/${row.report_id}`,
          row.completed_at ?? '',
          row.imo_number ?? '',
        ]),
      ];
      const today = new Date().toISOString().slice(0, 10);
      downloadCsvFile(rows, `screening-batch-${batch.id.slice(0, 8)}-${today}.csv`);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Export failed');
      Sentry.captureException(e);
      notifications.show({ title: 'Could not export register', message: e.message, color: 'red' });
    } finally {
      setExporting(false);
    }
  };

  if (isLoading && !batch) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (error || !batch) {
    return (
      <Container size="xl" py="md">
        <Text c="red">Could not load screening batch{error ? `: ${error.message}` : ''}.</Text>
      </Container>
    );
  }

  const { counts } = batch;
  const done = counts.completed + counts.failed;
  const totalPages = Math.ceil(batch.reports_total / DETAIL_PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Stack>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <PageTitle title={batch.label} />
            <Group gap="xs">
              <StatusBadge
                label={BATCH_STATUS_LABELS[batch.status]}
                color={BATCH_STATUS_COLORS[batch.status]}
              />
              <Text size="sm" c="dimmed">
                {teamName} · window {batch.max_in_flight} · {counts.total} subjects
              </Text>
            </Group>
          </Stack>
          <Group>
            <Tooltip label="Max reports in flight at once. Bounded by the API's DB headroom — 5 is the safe production default; raise gradually.">
              <Group gap={4} wrap="nowrap">
                <NumberInput
                  aria-label="Dispatch window"
                  min={1}
                  max={100}
                  w={90}
                  placeholder={String(batch.max_in_flight)}
                  value={windowDraft}
                  onChange={setWindowDraft}
                />
                <Button
                  variant="default"
                  disabled={typeof windowDraft !== 'number' || windowDraft === batch.max_in_flight}
                  onClick={() => {
                    if (typeof windowDraft === 'number') {
                      void setWindow(windowDraft).then(() => setWindowDraft(''));
                    }
                  }}
                >
                  Set window
                </Button>
              </Group>
            </Tooltip>
            <Button
              variant="default"
              leftSection={<IconTableExport size={16} />}
              loading={exporting}
              onClick={() => void exportRegister()}
            >
              Export register
            </Button>
            {zipStatus?.status === 'complete' ? (
              <Button.Group>
                <Button
                  variant="default"
                  leftSection={<IconFileZip size={16} />}
                  onClick={() => void saveZip()}
                  data-testid="batch-download-zip"
                >
                  Download zip ({zipStatus.done})
                </Button>
                <Tooltip label="Rebuild the zip (e.g. after triaging reports)">
                  <Button
                    variant="default"
                    px={8}
                    onClick={() => void startZipBuild()}
                    data-testid="batch-rebuild-zip"
                  >
                    <IconRefresh size={16} />
                  </Button>
                </Tooltip>
              </Button.Group>
            ) : (
              <Button
                variant="default"
                leftSection={<IconFileZip size={16} />}
                loading={zipStatus?.status === 'running'}
                onClick={() => void startZipBuild()}
                data-testid="batch-download-all"
              >
                {zipStatus?.status === 'running'
                  ? `Building ${zipStatus.done}/${zipStatus.total}`
                  : 'Download all'}
              </Button>
            )}
            {batch.status === 'pending' ? (
              <Button leftSection={<IconPlayerPlay size={16} />} onClick={() => void start()}>
                Start
              </Button>
            ) : null}
            {batch.status === 'running' ? (
              <Button
                variant="light"
                color="yellow"
                leftSection={<IconPlayerPause size={16} />}
                onClick={() => void pause()}
              >
                Pause
              </Button>
            ) : null}
            {batch.status === 'paused' ? (
              <Button leftSection={<IconPlayerPlay size={16} />} onClick={() => void resume()}>
                Resume
              </Button>
            ) : null}
          </Group>
        </Group>

        <LogoPaper p={16}>
          <Stack gap={6}>
            <Progress.Root size="lg">
              <Progress.Section
                value={counts.total ? (counts.completed / counts.total) * 100 : 0}
                color="green"
              />
              <Progress.Section
                value={counts.total ? (counts.failed / counts.total) * 100 : 0}
                color="red"
              />
              <Progress.Section
                value={counts.total ? (counts.in_flight / counts.total) * 100 : 0}
                color="blue"
              />
            </Progress.Root>
            <Text size="sm" c="dimmed">
              {done}/{counts.total} done · {counts.in_flight} in flight · {counts.pending} pending
              {counts.failed > 0 ? ` · ${counts.failed} failed` : ''}
            </Text>
            <RatingTallies ratings={counts.ratings} />
          </Stack>
        </LogoPaper>

        <LogoPaper>
          <Group justify="space-between" mb="sm">
            <TextInput
              aria-label="Search register"
              placeholder="Search subject, reference or codename…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              w={300}
              data-testid="batch-register-search"
            />
            <Group>
              <Select
                aria-label="Filter by report status"
                placeholder="All statuses"
                data={Object.entries(REPORT_STATUS)
                  .filter(([value]) => value !== 'processing_feedback')
                  .map(([value, meta]) => ({ value, label: meta.label }))}
                value={status || null}
                onChange={(value) => updateParams({ status: value ?? '', page: 1 })}
                clearable
                w={170}
                data-testid="batch-status-filter"
              />
              <Select
                aria-label="Filter by rating"
                placeholder="All ratings"
                data={RATING_FILTER_OPTIONS}
                value={rating || null}
                onChange={(value) => updateParams({ rating: value ?? '', page: 1 })}
                clearable
                w={180}
                data-testid="batch-rating-filter"
              />
              <Select
                aria-label="Filter by high-risk jurisdiction"
                placeholder="HRJ: all"
                data={[
                  { value: 'true', label: 'HRJ flagged' },
                  { value: 'false', label: 'Not flagged' },
                ]}
                value={hrjFilter || null}
                onChange={(value) => updateParams({ hrj: value ?? '', page: 1 })}
                clearable
                w={160}
                data-testid="batch-hrj-filter"
              />
            </Group>
          </Group>
          <Table.ScrollContainer minWidth={1000}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <SortableTh label="Reference" field="reference" {...sortProps} />
                  <SortableTh label="Subject" field="subject_name" {...sortProps} />
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Country / Flag</Table.Th>
                  <Table.Th>IMO</Table.Th>
                  <SortableTh label="Status" field="status" {...sortProps} />
                  <SortableTh label="Rating" field="rating" {...sortProps} />
                  <Table.Th>Codename</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {batch.reports.map((row) => {
                  const status = REPORT_STATUS[row.lifecycle_stage] ?? {
                    label: row.lifecycle_stage,
                    color: 'gray',
                  };
                  const isComplete = row.lifecycle_stage === 'completed';
                  return (
                    <Table.Tr
                      key={row.report_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/reports/${row.report_id}`)}
                    >
                      <Table.Td>{row.reference ?? '—'}</Table.Td>
                      <Table.Td>
                        <Text fw={500}>{row.subject_name}</Text>
                      </Table.Td>
                      <Table.Td>{row.subject_type ?? '—'}</Table.Td>
                      <Table.Td>{row.jurisdictions.join(', ') || '—'}</Table.Td>
                      <Table.Td>{row.imo_number ?? '—'}</Table.Td>
                      <Table.Td>
                        <StatusBadge label={status.label} color={status.color} />
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <RatingBadge
                            rating={row.screening_rating}
                            counts={row.screening_counts}
                            provisional={row.provisional}
                          />
                          {row.high_risk_jurisdictions.length > 0 ? (
                            <Tooltip
                              label={`High-risk jurisdiction: ${row.high_risk_jurisdictions.join(', ')}`}
                            >
                              <Badge size="xs" color="orange" variant="light">
                                HRJ
                              </Badge>
                            </Tooltip>
                          ) : null}
                        </Group>
                      </Table.Td>
                      <Table.Td>{row.codename ?? '—'}</Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        {isComplete ? (
                          <Group gap={4} wrap="nowrap">
                            {/* Markdown-kind reports have no .docx content
                                version (the endpoint 409s), so the DOCX
                                download only exists for docx-kind reports. */}
                            {row.content_kind === 'docx' ? (
                              <Tooltip label="Download DOCX">
                                <ActionIcon
                                  variant="subtle"
                                  onClick={() => void download(row, 'docx')}
                                >
                                  <IconDownload size={16} />
                                </ActionIcon>
                              </Tooltip>
                            ) : null}
                            <Tooltip label="Download PDF">
                              <ActionIcon
                                variant="subtle"
                                onClick={() => void download(row, 'pdf')}
                              >
                                <IconFileTypePdf size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 ? (
            <Group justify="center" mt="md">
              <Pagination
                value={page}
                onChange={(value) => updateParams({ page: value })}
                total={totalPages}
              />
            </Group>
          ) : null}
        </LogoPaper>
      </Stack>
    </Container>
  );
}
